import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Quote, Check, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { WORK_TYPES, RELATIONSHIPS } from "../data/content";
import { Eyebrow, Reveal, errMsg } from "./shared";
import { PlanetDot, AstrolabeGlyph } from "./motifs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { first_name: "", last_name: "", title: "", relationship: "", company: "", work_type: "", context: "", testimonial: "", email: "", linkedin: "", is_real_person: false, consents: false };

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">{label}</span>
      <input {...props} className="mt-1.5 w-full rounded-xl border border-border bg-linen/50 px-4 py-3 text-caramel outline-none focus:border-terracotta transition-colors" />
    </label>
  );
}

function SubmitModal({ open, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polished, setPolished] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function polishNow() {
    if (polishing) return;
    if ((f.testimonial || "").trim().length < 25) {
      toast.error("Write a sentence or two first, then I can polish it.");
      return;
    }
    setPolishing(true);
    setPolished("");
    try {
      const r = await axios.post(`${API}/testimonials/polish`, {
        text: f.testimonial,
        first_name: f.first_name,
        relationship: f.relationship,
      });
      setPolished(r.data?.polished || "");
    } catch (e) {
      toast.error(errMsg(e, "Could not polish right now."));
    } finally {
      setPolishing(false);
    }
  }

  useEffect(() => { if (open) { setStep(0); setF(empty); setPolished(""); setPolishing(false); } }, [open]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) { document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden"; }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  const valid = () => {
    if (step === 0) return f.first_name && f.last_name && f.title && f.relationship && f.company;
    if (step === 1) return f.work_type && f.testimonial.length > 15;
    if (step === 2) return f.is_real_person && f.consents && /\S+@\S+\.\S+/.test(f.email);
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/testimonials`, f);
      setStep(3);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Something went wrong. Please try again."));
    } finally { setBusy(false); }
  };

  const steps = ["About You", "Our Work Together", "Verification", "Done"];

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:p-8 overflow-y-auto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="testimonial-modal">
          <div className="fixed inset-0 bg-warmdark/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} className="relative bg-ivory rounded-[1.4rem] w-full max-w-lg my-4 border border-border p-6 sm:p-8" role="dialog" aria-modal="true" aria-label="Submit a testimonial">
            <button onClick={onClose} aria-label="Close" data-testid="testimonial-close" className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full hover:bg-linen text-caramel"><X size={20} /></button>

            <div className="font-mono-tech text-[11px] tracking-widest uppercase text-terracotta">Step {Math.min(step + 1, 4)} of 4 · {steps[step]}</div>
            <div className="mt-3 mb-6 h-1.5 rounded-full bg-linen overflow-hidden">
              <div className="h-full bg-terracotta rounded-full transition-all duration-300" style={{ width: `${((step + 1) / 4) * 100}%` }} />
            </div>

            {step === 0 && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="First name" value={f.first_name} onChange={set("first_name")} data-testid="t-first" />
                <Field label="Last name" value={f.last_name} onChange={set("last_name")} data-testid="t-last" />
                <div className="col-span-2"><Field label="Title / role" value={f.title} onChange={set("title")} data-testid="t-title" /></div>
                <div className="col-span-2"><Field label="Company" value={f.company} onChange={set("company")} data-testid="t-company" /></div>
                <label className="col-span-2 block">
                  <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">How do you know Barbara?</span>
                  <select value={f.relationship} onChange={set("relationship")} data-testid="t-relationship" className="mt-1.5 w-full rounded-xl border border-border bg-linen/50 px-4 py-3 text-caramel outline-none focus:border-terracotta">
                    <option value="">Select one</option>
                    {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">Type of work</span>
                  <select value={f.work_type} onChange={set("work_type")} data-testid="t-worktype" className="mt-1.5 w-full rounded-xl border border-border bg-linen/50 px-4 py-3 text-caramel outline-none focus:border-terracotta">
                    <option value="">Select one</option>
                    {WORK_TYPES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
                <Field label="Context (optional)" value={f.context} onChange={set("context")} placeholder="Where and when did you work together?" data-testid="t-context" />
                <label className="block">
                  <span className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted">Your testimonial</span>
                  <textarea value={f.testimonial} onChange={set("testimonial")} rows={5} data-testid="t-body" maxLength={1200} className="mt-1.5 w-full rounded-xl border border-border bg-linen/50 px-4 py-3 text-caramel outline-none focus:border-terracotta resize-none" placeholder="Share what it was like working with Barbara..." />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted">
                      {f.testimonial.length}/1200
                    </span>
                    <button
                      type="button"
                      onClick={polishNow}
                      disabled={polishing || f.testimonial.trim().length < 25}
                      data-testid="t-polish"
                      data-cosmic="true"
                      className="inline-flex items-center gap-1.5 rounded-full border border-brass/40 bg-parchment text-caramel px-3.5 py-1.5 text-xs font-mono-tech tracking-wide hover:bg-terracotta hover:text-ivory hover:border-terracotta disabled:opacity-40 disabled:hover:bg-parchment disabled:hover:text-caramel disabled:hover:border-brass/40 transition-colors"
                    >
                      <Sparkles size={12} />
                      {polishing ? "Polishing…" : "Polish with AI"}
                    </button>
                  </div>
                  {polished && (
                    <div className="mt-3 rounded-2xl border border-brass/40 bg-parchment p-4" data-testid="t-polished-panel">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={12} className="text-terracotta" />
                        <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-terracotta">Suggested wording</span>
                      </div>
                      <p className="text-sm text-caramel/90 leading-relaxed italic">"{polished}"</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => { setF({ ...f, testimonial: polished }); setPolished(""); toast.success("Applied suggested wording."); }}
                          data-testid="t-polish-accept"
                          data-cosmic="true"
                          className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-ivory px-4 py-1.5 text-xs font-semibold hover:bg-warmdark transition-colors"
                        >
                          <Check size={12} /> Use suggestion
                        </button>
                        <button
                          type="button"
                          onClick={() => setPolished("")}
                          data-testid="t-polish-reject"
                          className="inline-flex items-center gap-1.5 rounded-full border border-brass/40 text-caramel px-4 py-1.5 text-xs hover:border-terracotta hover:text-terracotta transition-colors"
                        >
                          Keep mine
                        </button>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Field label="Email" type="email" value={f.email} onChange={set("email")} data-testid="t-email" />
                <Field label="LinkedIn URL (optional)" value={f.linkedin} onChange={set("linkedin")} data-testid="t-linkedin" />
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={f.is_real_person} onChange={(e) => setF({ ...f, is_real_person: e.target.checked })} data-testid="t-real" className="mt-1 accent-[#B8622A] w-4 h-4" />
                  <span className="text-sm text-caramel/85">I confirm I am a real person and this testimonial reflects my genuine experience.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={f.consents} onChange={(e) => setF({ ...f, consents: e.target.checked })} data-testid="t-consent" className="mt-1 accent-[#B8622A] w-4 h-4" />
                  <span className="text-sm text-caramel/85">I consent to Barbara displaying this testimonial on her website.</span>
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 grid place-items-center rounded-full bg-sage/30 text-forest mb-5"><Check size={30} /></div>
                <h3 className="font-display text-3xl text-caramel font-light">Thank you, truly.</h3>
                <p className="text-caramel/75 mt-3">Your testimonial is on its way to Barbara for review. It means the world.</p>
                <button onClick={onClose} data-testid="testimonial-finish" className="mt-6 rounded-full bg-terracotta text-ivory px-6 py-3 hover:bg-warmdark transition-colors">Close</button>
              </div>
            )}

            {step < 3 && (
              <div className="mt-7 flex items-center justify-between">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-1 text-caramel/70 disabled:opacity-30 hover:text-terracotta transition-colors" data-testid="t-back"><ChevronLeft size={18} /> Back</button>
                {step < 2 ? (
                  <button onClick={() => valid() ? setStep((s) => s + 1) : toast.error("Please complete this step.")} data-testid="t-next" className="inline-flex items-center gap-1 rounded-full bg-terracotta text-ivory px-6 py-2.5 hover:bg-warmdark transition-colors">Next <ChevronRight size={18} /></button>
                ) : (
                  <button onClick={() => valid() ? submit() : toast.error("Please verify and add your email.")} disabled={busy} data-testid="t-submit" className="rounded-full bg-terracotta text-ivory px-6 py-2.5 hover:bg-warmdark transition-colors disabled:opacity-60">{busy ? "Sending..." : "Submit"}</button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  const load = () => axios.get(`${API}/testimonials`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const perSlide = typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 3;
  const pages = Math.max(1, Math.ceil(items.length / perSlide));
  const slice = items.slice(page * perSlide, page * perSlide + perSlide);

  return (
    <section id="testimonials" className="relative py-24 sm:py-32 bg-ivory overflow-hidden">
      <div className="pointer-events-none absolute inset-0 topo-lines opacity-40" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6 mb-12">
          <Reveal><Eyebrow n={8}>Testimonials</Eyebrow></Reveal>
          {items.length > perSlide && (
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setPage((p) => (p - 1 + pages) % pages)} data-testid="testimonial-prev" aria-label="Previous" className="w-11 h-11 grid place-items-center rounded-full bg-linen hover:bg-blush text-caramel transition-colors"><ChevronLeft size={20} /></button>
              <button onClick={() => setPage((p) => (p + 1) % pages)} data-testid="testimonial-next" aria-label="Next" className="w-11 h-11 grid place-items-center rounded-full bg-linen hover:bg-blush text-caramel transition-colors"><ChevronRight size={20} /></button>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <Reveal>
            <div className="rounded-[1.4rem] border border-dashed border-border bg-linen/50 p-10 sm:p-14 text-center">
              <Quote size={36} className="mx-auto text-terracotta/60" />
              <p className="mt-4 font-display text-2xl sm:text-3xl text-caramel font-light max-w-xl mx-auto">Kind words are gathering. If we have worked together, I would be honored to hear from you.</p>
            </div>
          </Reveal>
        ) : (
          <div className="grid md:grid-cols-3 gap-6" data-testid="testimonial-cards">
            {slice.map((t) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-2xl border border-border bg-linen p-7 flex flex-col observatory-frame">
                <AstrolabeGlyph size={14} color="#B08830" />
                <div className="flex text-gold mb-3 mt-3">{[0,1,2,3,4].map((i) => <Star key={i} size={16} fill="currentColor" />)}</div>
                <p className="text-caramel/85 leading-relaxed flex-1">"{t.testimonial}"</p>
                <div className="mt-5 pt-4 border-t border-brass/30">
                  <div className="font-semibold text-caramel flex items-center gap-2"><PlanetDot /> {t.first_name} {t.last_name}</div>
                  <div className="font-mono-tech text-[11px] tracking-wide uppercase text-warmmuted mt-0.5">{t.title} · {t.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <button onClick={() => setOpen(true)} data-testid="testimonial-submit-open" data-cosmic="true" className="inline-flex items-center gap-2 rounded-full bg-terracotta text-ivory px-7 py-3.5 font-medium hover:bg-warmdark transition-colors">
            Submit a testimonial
          </button>
        </div>
      </div>
      <SubmitModal open={open} onClose={() => setOpen(false)} onDone={load} />
    </section>
  );
}
